export const DELIVERY_TYPES = ['regular', 'express', 'pickup'];

/** All sources used in filters (includes WooCommerce imports). */
export const ORDER_SOURCES = [
  'phone_call',
  'facebook',
  'messenger',
  'whatsapp',
  'tiktok',
  'instagram',
  'offline',
  'upsell',
  'website',
  'live_chat',
  'telesales',
  'woocommerce',
  'captain',
  'other',
];

/** Sources selectable when manually creating an order (not WooCommerce sync). */
export const MANUAL_ORDER_SOURCES = ORDER_SOURCES.filter(
  source => source !== 'woocommerce' && source !== 'captain'
);

export const ORDER_STATUSES = [
  'pending',
  'on-hold',
  'approved',
  'processing',
  'ready-to-go',
  'in-transit',
  'delivered',
  'returned',
  'cancelled',
  'flagged',
];

export const defaultOrderFilters = () => ({
  filterDeliveryTypes: [],
  filterOrderStatuses: [],
  filterOrderSources: [],
  filterDateCreation: { startDate: '', endDate: '' },
  filterDateShipping: { startDate: '', endDate: '' },
  filterDateTransition: { startDate: '', endDate: '' },
  filterRangeInvoice: { startInvoice: '', endInvoice: '' },
  filterRangeOrderValue: { min: '', max: '' },
  filterRangeProductCount: { minQty: '', maxQty: '', sku: '' },
  filterDeliveryPartnerIds: [],
  filterCourierStatuses: [],
  filterPickupAddressId: '',
  filterCustomerTags: [],
  filterHasInternalNotes: null,
  filterHasDiscount: null,
  filterIsAdvancePaid: null,
  filterIsInvoicePrinted: null,
  filterHasUpsell: null,
  filterDuplicatePhones: null,
  filterDuplicatePhone: '',
  filterDuplicatePhoneMinCount: '',
  filterAssignedTo: '',
  filterAssignedOn: { startDate: '', endDate: '' },
  filterActionByUserId: '',
  filterActionStatus: '',
});

const hasValue = value => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return Object.values(value).some(entry => hasValue(entry));
  }
  return true;
};

const cleanDateRange = range => {
  if (!range) return null;
  const startDate = range.startDate?.trim?.() || range.startDate || '';
  const endDate = range.endDate?.trim?.() || range.endDate || '';
  if (!startDate && !endDate) return null;
  return { startDate, endDate };
};

const cleanNumericRange = (range, keys) => {
  if (!range) return null;
  const cleaned = keys.reduce((acc, key) => {
    const value = range[key];
    if (value !== null && value !== undefined && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});
  return Object.keys(cleaned).length ? cleaned : null;
};

const appendBoolean = (payload, key, value) => {
  if (value === true || value === false) {
    payload[key] = value;
  }
};

export const buildFilterPayload = filters => {
  const payload = {};

  if (filters.filterDeliveryTypes?.length) {
    payload.filter_delivery_types = [...filters.filterDeliveryTypes];
  }
  if (filters.filterOrderStatuses?.length) {
    payload.filter_order_statuses = [...filters.filterOrderStatuses];
  }
  if (filters.filterOrderSources?.length) {
    payload.filter_order_sources = [...filters.filterOrderSources];
  }

  const creation = cleanDateRange(filters.filterDateCreation);
  if (creation) {
    payload.filter_date_creation = {
      start_date: creation.startDate,
      end_date: creation.endDate,
    };
  }

  const shipping = cleanDateRange(filters.filterDateShipping);
  if (shipping) {
    payload.filter_date_shipping = {
      start_date: shipping.startDate,
      end_date: shipping.endDate,
    };
  }

  const transition = cleanDateRange(filters.filterDateTransition);
  if (transition) {
    payload.filter_date_transition = {
      start_date: transition.startDate,
      end_date: transition.endDate,
    };
  }

  const invoice = cleanNumericRange(filters.filterRangeInvoice, [
    'startInvoice',
    'endInvoice',
  ]);
  if (invoice) {
    payload.filter_range_invoice = {
      start_invoice: invoice.startInvoice,
      end_invoice: invoice.endInvoice,
    };
  }

  const orderValue = cleanNumericRange(filters.filterRangeOrderValue, [
    'min',
    'max',
  ]);
  if (orderValue) payload.filter_range_order_value = orderValue;

  const productCount = cleanNumericRange(filters.filterRangeProductCount, [
    'minQty',
    'maxQty',
    'sku',
  ]);
  if (productCount) {
    payload.filter_range_product_count = {
      min_qty: productCount.minQty,
      max_qty: productCount.maxQty,
      sku: productCount.sku,
    };
  }

  if (filters.filterDeliveryPartnerIds?.length) {
    payload.filter_delivery_partner_ids = [...filters.filterDeliveryPartnerIds];
  }
  if (filters.filterCourierStatuses?.length) {
    payload.filter_courier_statuses = [...filters.filterCourierStatuses];
  }
  if (filters.filterPickupAddressId) {
    payload.filter_pickup_address_id = filters.filterPickupAddressId;
  }
  if (filters.filterCustomerTags?.length) {
    payload.filter_customer_tags = [...filters.filterCustomerTags];
  }

  appendBoolean(
    payload,
    'filter_has_internal_notes',
    filters.filterHasInternalNotes
  );
  appendBoolean(payload, 'filter_has_discount', filters.filterHasDiscount);
  appendBoolean(payload, 'filter_is_advance_paid', filters.filterIsAdvancePaid);
  appendBoolean(
    payload,
    'filter_is_invoice_printed',
    filters.filterIsInvoicePrinted
  );
  appendBoolean(payload, 'filter_has_upsell', filters.filterHasUpsell);

  const dupPhone = String(filters.filterDuplicatePhone || '').trim();
  if (dupPhone) payload.filter_duplicate_phone = dupPhone;

  const dupMin = String(filters.filterDuplicatePhoneMinCount || '').trim();
  if (dupMin) payload.filter_duplicate_phone_min_count = Number(dupMin);

  // Min count implies duplicate mode; checkbox alone also enables it.
  if (filters.filterDuplicatePhones === true || dupMin) {
    payload.filter_duplicate_phones = true;
  }

  if (filters.filterAssignedTo) {
    payload.filter_assigned_to = filters.filterAssignedTo;
  }

  const assignedOn = cleanDateRange(filters.filterAssignedOn);
  if (assignedOn) {
    payload.filter_assigned_on = {
      start_date: assignedOn.startDate,
      end_date: assignedOn.endDate,
    };
  }

  if (filters.filterActionByUserId) {
    payload.filter_action_by_user_id = filters.filterActionByUserId;
  }
  if (filters.filterActionStatus) {
    payload.filter_action_status = filters.filterActionStatus;
  }

  return payload;
};

export const hasActiveOrderFilters = filters =>
  hasValue(buildFilterPayload(filters));

export const formatDateRangeLabel = (range, t) => {
  const cleaned = cleanDateRange(range);
  if (!cleaned) return '';
  const pretty = value =>
    String(value || '')
      .replace('T', ' ')
      .slice(0, 16);
  if (cleaned.startDate && cleaned.endDate) {
    return `${pretty(cleaned.startDate)} – ${pretty(cleaned.endDate)}`;
  }
  if (cleaned.startDate) {
    return `${t('WEB_ORDER.ORDERS.FROM')} ${pretty(cleaned.startDate)}`;
  }
  return `${t('WEB_ORDER.ORDERS.TO')} ${pretty(cleaned.endDate)}`;
};
